import { computed, ref } from 'vue';
import { useFilterSidePanel, useDrawer } from '@dashhudson/dashing-ui';
import { useSocialListeningStore } from '@/stores/social-listening';
import { SOCIAL_LISTENING_DRAWERS } from '@/app/socialListening/constants';
import { sortFilterOptionsByLabel } from '@/app/socialListening/utils/filter-utils';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import { useLocationOptions } from './useLocationOptions';

/**
 * Shared composable for managing filter panels across social listening features.
 * Provides common utilities and state management for filter panels.
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.filterMap - Filter map from LISTENING_SCOPED_FILTERS (e.g., TRENDS, TOPIC, COMPARISON)
 * @returns {Object} Filter panel manager utilities
 */
export function useFilterPanelManager(config) {
  const { filterMap } = config;

  // Validate required parameters
  if (!filterMap) {
    throw new Error('useFilterPanelManager requires filterMap');
  }

  // Core composables
  const listeningStore = useSocialListeningStore();
  const {
    scopedFilters: appliedFilters,
    internalFilters,
    internalUnappliedFilters,
    apply,
    reset,
  } = useFilterSidePanel();
  const { currentDrawer } = useDrawer();
  const { lookupLocationsInOptions, stripLocationObject } = useLocationOptions();

  // Local state
  const locationSearchListKey = ref(0);

  /**
   * Check if any drawer is currently open
   */
  const isDrawerOpen = computed(() => !!currentDrawer.value);

  /**
   * Check if Top Posts drawer is open (requires special filter handling)
   */
  const isTopPostsDrawerOpen = computed(
    () => currentDrawer.value?.name === SOCIAL_LISTENING_DRAWERS.TOP_POST,
  );

  /**
   * Check if Trending Keywords drawer is open
   */
  const isTrendingKeywordsDrawerOpen = computed(
    () => currentDrawer.value?.name === SOCIAL_LISTENING_DRAWERS.TRENDING_KEYWORD,
  );

  /**
   * Current keyword filter for display in the UI
   */
  const selectedKeywordFilter = computed(() => {
    const selectedKeyword = listeningStore?.topicKeywordFilter;
    if (selectedKeyword?.word) {
      return {
        rule: selectedKeyword.rule,
        keyword: selectedKeyword.word,
        title: `Keyword ${selectedKeyword?.rule.toLowerCase().replace('s', 'd')}`,
      };
    }
    return null;
  });

  /**
   * Check if keyword filter is currently applied
   */
  const hasKeywordFilter = computed(() => !!internalFilters[filterMap.scope]?.keyword);

  /**
   * Industry options sorted by label for filter dropdown
   */
  const industryOptions = computed(() => {
    if (listeningStore.industries?.length) {
      const industries = listeningStore.industries.map((industry) => ({
        label: industry.name,
        value: industry.id,
      }));
      return sortFilterOptionsByLabel(industries);
    }
    return [];
  });

  /**
   * Reset the location search list key to force re-render
   */
  function resetLocationKey() {
    locationSearchListKey.value += 1;
  }

  /**
   * Check if date range is valid (has either rolling offset or both dates)
   * @param {Object} sourceCreated - Date range object
   * @returns {boolean} True if date range is valid
   */
  function isDateRangeValid(sourceCreated) {
    if (!sourceCreated) return false;
    const { rollingDateRangeOffset, onOrAfter, onOrBefore } = sourceCreated;
    return rollingDateRangeOffset !== undefined || (onOrAfter && onOrBefore);
  }

  /**
   * Detect if filters have changed between two filter objects
   * @param {Object} newFilters - New filter values
   * @param {Object} oldFilters - Previous filter values
   * @returns {boolean} True if filters have changed
   */
  function detectFilterChanges(newFilters, oldFilters) {
    const { sourceCreated: newSourceCreated, ...newFilterData } = newFilters || {};
    const { sourceCreated: oldSourceCreated, ...oldFilterData } = oldFilters || {};

    // Check if non-date filters changed
    if (!isEqual(omitBy(newFilterData, isEmpty), omitBy(oldFilterData, isEmpty))) {
      return true;
    }

    // Check if date filter changed (including rolling date range offset)
    if (
      (newSourceCreated?.rollingDateRangeOffset &&
        newSourceCreated?.rollingDateRangeOffset !== oldSourceCreated?.rollingDateRangeOffset) ||
      !isEqual(newSourceCreated, oldSourceCreated)
    ) {
      return true;
    }

    return false;
  }

  /**
   * Build filter overrides with cleaned location data
   * @param {Object} filters - Filters to clean
   * @returns {Object} Cleaned filter overrides
   */
  function buildFilterOverrides(filters) {
    const { sourceCreated, ...otherFilters } = filters || {};
    const strippedLocations = (otherFilters?.locations || []).map(stripLocationObject);
    return omitBy({ sourceCreated, ...otherFilters, locations: strippedLocations }, isEmpty);
  }

  return {
    // State
    locationSearchListKey,
    appliedFilters,
    internalFilters,
    internalUnappliedFilters,

    // Composable instances
    apply,
    reset,

    // Drawer reference
    currentDrawer,

    // Drawer state
    isDrawerOpen,
    isTopPostsDrawerOpen,
    isTrendingKeywordsDrawerOpen,

    // Keyword filter
    selectedKeywordFilter,
    hasKeywordFilter,

    // Common options
    industryOptions,

    // Location utilities
    lookupLocationsInOptions,
    stripLocationObject,
    resetLocationKey,

    // Date validation
    isDateRangeValid,

    // Change detection
    detectFilterChanges,
    buildFilterOverrides,
  };
}
